import { useSyncExternalStore } from 'react'
import { skipToken, useQuery } from '@tanstack/react-query'

import { fetchIdentity, getSessionEpoch, subscribe, useAuth } from './auth'
import { IAM_PERMISSIONS, can, type PermissionKey } from './permissions'
import { useSelectedEntityId } from './use-selected-entity'

export function usePermissions(): {
  can: (key: PermissionKey) => boolean
  ready: boolean
} {
  const { user } = useAuth()
  const selectedEntityId = useSelectedEntityId()
  const scoped =
    user?.permissions !== undefined && user.entityId === selectedEntityId
  return {
    ready: scoped,
    can: (key) => scoped && can(user?.permissions, key),
  }
}

export function usePermissionsAt(entityId?: string | null): ReturnType<typeof usePermissions> {
  const { user, tokenContext } = useAuth()
  const selectedEntityId = useSelectedEntityId()
  const target = entityId === undefined ? selectedEntityId : entityId
  const epoch = useSyncExternalStore(subscribe, getSessionEpoch, getSessionEpoch)
  const scoped = target === selectedEntityId && user?.entityId === target && user?.permissions !== undefined
  const fetchOwner = target !== null && user !== null && target !== selectedEntityId
  const owner = useQuery({
    queryKey: [
      'entity-permissions', epoch,
      tokenContext.principal,
      tokenContext.generation,
      user?.userId,
      user?.impersonation?.actorUserId,
      target,
    ],
    queryFn: fetchOwner ? () => fetchIdentity(target) : skipToken,
    placeholderData: (previous, query) =>
      tokenContext.principal !== undefined && query?.queryKey[1] === epoch && query.queryKey[2] === tokenContext.principal
        ? previous : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const ready = scoped || fetchOwner && !owner.isError &&
    owner.data?.userId === user.userId && owner.data?.entityId === target &&
    owner.data?.impersonation?.actorUserId === user.impersonation?.actorUserId &&
    owner.data?.permissions !== undefined
  const permissions = scoped ? user.permissions : owner.data?.permissions
  return {
    ready,
    can: (key) => ready && can(permissions, key),
  }
}

export function useCanAdministerIam(): boolean {
  const { can: allows } = usePermissions()
  return allows(IAM_PERMISSIONS.manage)
}
